import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSlidersH, faArrowRight } from '@fortawesome/free-solid-svg-icons';

const StabilityRefinementMock = () => (
  <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono" style={{ background: '#0f1923' }}>
    {/* Modal header */}
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]" style={{ background: '#0c1520' }}>
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={faSlidersH} className="text-sm text-gray-400" />
        <span className="text-sm font-semibold text-white">Stability Refinement</span>
      </div>
      <span className="text-gray-500 text-xs">EMA Cross -- BTCUSDT 1h</span>
    </div>

    {/* Two-column body */}
    <div className="p-3 grid grid-cols-2 gap-3">
      {/* Left column: Primary Stable Region */}
      <div className="space-y-3">
        {/* Hero card */}
        <div className="rounded-lg bg-emerald-900/20 border border-emerald-800/40 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-400 text-xs" />
            <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wide">Primary Stable Region</span>
          </div>
          {/* 2x2 stats grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/[0.04] rounded p-2">
              <p className="text-[8px] text-gray-500 uppercase tracking-wider">Combinations</p>
              <p className="text-sm font-bold text-white">24</p>
            </div>
            <div className="bg-white/[0.04] rounded p-2">
              <p className="text-[8px] text-gray-500 uppercase tracking-wider">Robustness</p>
              <p className="text-sm font-bold text-emerald-300">78%</p>
            </div>
            <div className="bg-white/[0.04] rounded p-2">
              <p className="text-[8px] text-gray-500 uppercase tracking-wider">Variance</p>
              <p className="text-sm font-bold text-white">0.12</p>
            </div>
            <div className="bg-white/[0.04] rounded p-2">
              <p className="text-[8px] text-gray-500 uppercase tracking-wider">Sensitivity</p>
              <p className="text-sm font-bold text-white">Low</p>
            </div>
          </div>
        </div>

        {/* Why Selected */}
        <div className="bg-white/[0.03] rounded-lg p-3">
          <h4 className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Why Selected
          </h4>
          <ul className="space-y-1 text-[10px] text-gray-400">
            <li className="flex items-start gap-1.5">
              <span className="text-gray-500 mt-px">&#8226;</span>
              <span>Highest density cluster in parameter space</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-gray-500 mt-px">&#8226;</span>
              <span>Controlled drawdown behaviour across region</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-gray-500 mt-px">&#8226;</span>
              <span>Performance remains consistent under parameter variations</span>
            </li>
          </ul>
        </div>

        {/* Selected Parameters */}
        <div className="bg-white/[0.03] rounded-lg p-3">
          <h4 className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Selected Parameters
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-[9px] text-gray-500">Fast Length</span>
              <p className="text-base font-semibold text-white">12</p>
            </div>
            <div>
              <span className="text-[9px] text-gray-500">Slow Length</span>
              <p className="text-base font-semibold text-white">24</p>
            </div>
          </div>
          <p className="text-[9px] text-gray-500 mt-2">
            These parameters will be used as the starting point for Walk-Forward Validation.
          </p>
        </div>
      </div>

      {/* Right column: Chart + alternates */}
      <div className="space-y-3">
        {/* Chart type toggle */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide">
              Parameter Stability Map
            </h3>
            <div className="flex items-center gap-0.5 bg-white/10 rounded-md p-0.5">
              <span className="px-2 py-0.5 rounded text-[9px] font-medium bg-white/20 text-gray-100 shadow-sm">Heatmap</span>
              <span className="px-2 py-0.5 rounded text-[9px] font-medium text-gray-400">Lines</span>
              <span className="px-2 py-0.5 rounded text-[9px] font-medium text-gray-400">Strips</span>
            </div>
          </div>
          {/* Parameter strips heatmap */}
          <div className="bg-white/[0.03] rounded-lg p-3 space-y-3">
            {/* Fast Length strip */}
            <div className="flex items-center gap-2">
              <span className="text-[8px] text-gray-400 w-14 text-right shrink-0">Fast Length</span>
              <div className="flex-1 flex gap-[2px]">
                {[
                  'bg-gray-600/40', 'bg-gray-600/40', 'bg-gray-600/40',
                  'bg-emerald-600/50', 'bg-emerald-500/60', 'bg-emerald-400/80',
                  'bg-emerald-400/90', 'bg-emerald-400/80', 'bg-emerald-500/60',
                  'bg-gray-600/40', 'bg-gray-600/40', 'bg-gray-600/40',
                  'bg-gray-600/40', 'bg-gray-600/40',
                ].map((c, i) => (
                  <div key={i} className="relative flex-1">
                    <div className={`h-5 rounded-[2px] ${c}`} />
                    {i === 6 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1.5 h-3 bg-white rounded-full opacity-90" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Slow Length strip */}
            <div className="flex items-center gap-2">
              <span className="text-[8px] text-gray-400 w-14 text-right shrink-0">Slow Length</span>
              <div className="flex-1 flex gap-[2px]">
                {[
                  'bg-emerald-600/50', 'bg-emerald-500/60', 'bg-emerald-500/70',
                  'bg-emerald-400/80', 'bg-emerald-400/90', 'bg-emerald-400/80',
                  'bg-emerald-500/60', 'bg-emerald-600/50', 'bg-gray-600/40',
                  'bg-gray-600/40', 'bg-gray-600/40', 'bg-gray-600/40',
                  'bg-gray-600/40', 'bg-gray-600/40',
                ].map((c, i) => (
                  <div key={i} className="relative flex-1">
                    <div className={`h-5 rounded-[2px] ${c}`} />
                    {i === 3 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1.5 h-3 bg-white rounded-full opacity-90" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Axis labels */}
            <div className="flex items-center gap-2">
              <span className="w-14 shrink-0" />
              <div className="flex-1 flex justify-between text-[7px] text-gray-600">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 text-[7px] text-gray-400">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-400/70" />
                <span>Primary region</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-emerald-400" />
                <span>Selected value</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-gray-600/50" />
                <span>Other results</span>
              </div>
            </div>
            {/* Description */}
            <p className="text-[7px] text-gray-500 leading-relaxed">
              Each row shows the distribution of parameter values. Brighter green indicates more combinations in the stable region. The white marker shows the selected centroid value for each parameter.
            </p>
          </div>
        </div>

        {/* Alternate Regions */}
        <div className="bg-white/[0.03] rounded-lg p-3">
          <span className="text-[9px] font-medium text-gray-400 flex items-center gap-1.5">
            Alternate Regions (2)
          </span>
          <div className="mt-2 space-y-1.5">
            <div className="pl-2.5 border-l-2 border-white/[0.06]">
              <p className="text-[9px] font-medium text-gray-300">Region A</p>
              <p className="text-[8px] text-gray-500">18 combinations &middot; Lower tolerance than Primary</p>
            </div>
            <div className="pl-2.5 border-l-2 border-white/[0.06]">
              <p className="text-[9px] font-medium text-gray-300">Region B</p>
              <p className="text-[8px] text-gray-500">12 combinations &middot; Similar tolerance to Primary</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className="flex items-center justify-end gap-3 px-4 py-2 border-t border-white/[0.06]" style={{ background: '#0c1520' }}>
      <span className="text-[10px] text-gray-500">Cancel</span>
      <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium text-white rounded-lg" style={{ backgroundColor: '#aa48ff' }}>
        Proceed with Robust Selection
        <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
      </div>
    </div>
  </div>
);

export default StabilityRefinementMock;
